// src/pages/Account/MySubscriptions.js

import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  Spinner,
  Badge,
  VStack,
  Button,
  Flex,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import { WarningIcon } from '@chakra-ui/icons';
import { Link } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import { useAuth } from '../../contexts/AuthContext';
import logo from '../../images/logo.png'; // o il percorso corretto del tuo logo
import AccountPageShell from './AccountPageShell';
import TagliandiFb from './TagliandiFb';

const MySubscriptions = () => {
  const [userDonations, setUserDonations] = useState([]);
  const [loadingDonationsUser, setLoadingDonationsUser] = useState(true);
  const [nRicevuteFb, setNRicevuteFb] = useState(0);
  const { userData } = useAuth();

  const getBase64ImageFromURL = async (url) => {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handlePrintDonationReceipt = async (donation) => {
    let logoData = '';
    try {
      logoData = await getBase64ImageFromURL(logo);
    } catch (e) {
      console.error('Logo load error', e);
    }

    // eslint-disable-next-line new-cap
    const doc = new jsPDF('p', 'pt', 'a4');
    const { width: pageWidth, height: pageHeight } = doc.internal.pageSize;
    const margin = 40;
    const lineHeight = 20;
    let y = 40;

    // colori
    const giallo = '#ffcc00';
    const blu = '#36a9e1';
    const viola = '#3b235a';

    // header giallo e logo
    doc.setFillColor(giallo);
    doc.rect(0, 0, pageWidth, 120, 'F');
    if (logoData)
      doc.addImage(logoData, 'PNG', pageWidth - margin - 100, 20, 100, 35);

    // dati associazione
    doc.setFontSize(11).setFont('helvetica', 'bold').setTextColor('#000');
    doc.text('CIL – Creatività in Libertà APS', margin, 30);
    doc.setFont('helvetica', 'normal');
    doc.text('CF/P.IVA: 94090260012', margin, 45);
    doc.text('Via GB Buschetti 18, 10023 Chieri (TO)', margin, 60);
    doc.text('info@cilassociazione.it | +39 353 468 2493', margin, 75);
    doc.text('www.cilassociazione.it', margin, 90);

    y = 140;
    doc
      .setDrawColor(blu)
      .setLineWidth(1)
      .line(margin, y, pageWidth - margin, y);
    y += 30;

    // titolo
    doc.setFontSize(16).setFont('helvetica', 'bold').setTextColor(viola);
    doc.text('Ricevuta di Pagamento Donazione', pageWidth / 2, y, {
      align: 'center',
    });
    y += 40;

    // dati donazione + socio/genitore
    // Estraggo createdAt e amount dalla posizione corretta
    const actualCreatedAt =
      donation.createdAt ||
      donation.attributes?.createdAt ||
      donation.data?.createdAt ||
      donation.data?.[0]?.createdAt;

    const actualAmount =
      donation.amount ||
      donation.attributes?.amount ||
      donation.data?.amount ||
      donation.data?.[0]?.amount;

    const { id, currency } = donation;
    const metadata =
      donation.metadata ||
      donation.attributes?.metadata ||
      donation.data?.metadata ||
      donation.data?.[0]?.metadata ||
      {};
    const createdAt = actualCreatedAt;
    const amount = actualAmount;

    // Validazione e parsing sicuro dell'importo
    let safeAmount = 0;
    try {
      if (amount !== null && amount !== undefined) {
        safeAmount = parseFloat(amount);
        if (Number.isNaN(safeAmount)) {
          safeAmount = 0;
        }
      }
    } catch (parseAmountError) {
      console.warn('Errore parsing amount, uso 0:', parseAmountError);
      safeAmount = 0;
    }

    // Validazione e parsing sicuro della data
    let donationDate;

    // Controllo se createdAt esiste
    if (!createdAt) {
      console.warn('createdAt è undefined, uso data corrente');
      donationDate = new Date();
    } else {
      try {
        // Forza l'uso della data createdAt
        donationDate = new Date(createdAt);

        // Controllo più robusto per la validità della data
        if (
          Number.isNaN(donationDate.getTime()) ||
          donationDate.getTime() === 0
        ) {
          // Prova a parsare la data in modo diverso
          const parts = createdAt.split('T')[0].split('-');
          if (parts.length === 3) {
            const [year, month, day] = parts;
            donationDate = new Date(
              parseInt(year, 10),
              parseInt(month, 10) - 1,
              parseInt(day, 10)
            );
          } else {
            donationDate = new Date();
          }
        }
      } catch (parseError) {
        donationDate = new Date();
      }
    }

    const dateStr = new Intl.DateTimeFormat('it-IT', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(donationDate);

    const socioName =
      `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
    const parentName = metadata?.parentName || metadata?.genitore || '';

    // Debug: log dei metadata per verificare il contenuto
    console.log('Metadata nella ricevuta:', metadata);

    // Determina la destinazione della donazione
    let destinazione = 'Donazione libera';
    if (metadata?.goalName) {
      destinazione = `Obiettivo: ${metadata.goalName}`;
    } else if (metadata?.donationItem) {
      destinazione = `Acquisto F&B: ${metadata.donationItem}`;
    }

    const rows = [
      ['Numero Ricevuta', `D${id || 'N/A'}`],
      ['Data', dateStr],
      ['Donatore', socioName || 'N/A'],
      ...(parentName ? [['Genitore', parentName]] : []),
      [
        'Importo',
        `${safeAmount.toFixed(2)} ${(currency || 'EUR').toUpperCase()}`,
      ],
      ['Destinazione', destinazione],
      ...(metadata?.donationItem
        ? [['Prodotto Acquistato', metadata.donationItem]]
        : []),
      ['Stato', 'Pagamento Confermato'],
    ];

    doc.setFontSize(12).setTextColor('#000');
    rows.forEach(([label, val], i) => {
      doc
        .setFont('helvetica', 'bold')
        .text(`${label}:`, margin, y + i * lineHeight);
      doc
        .setFont('helvetica', 'normal')
        .text(val.toString(), margin + 140, y + i * lineHeight);
    });
    y += rows.length * lineHeight + 30;

    // footer
    doc.setDrawColor(blu).line(margin, y, pageWidth - margin, y);
    doc.setFillColor(viola);
    doc.rect(0, pageHeight - 50, pageWidth, 50, 'F');
    doc.setFontSize(11).setFont('helvetica', 'bold').setTextColor('#fff');
    doc.text(
      'Grazie per sostenere CIL – Creatività in Libertà',
      pageWidth / 2,
      pageHeight - 25,
      { align: 'center' }
    );

    doc.save(`ricevuta_donazione_${id}.pdf`);
  };

  useEffect(() => {
    const loadUserDonations = async () => {
      setLoadingDonationsUser(true);
      try {
        const resp = await axios.get('/api/donations', {
          params: {
            'filters[user][id]': userData.id,
            populate: 'donation_goal',
            'pagination[pageSize]': 100,
          },
          withCredentials: true,
        });

        const donations = resp.data.data || [];

        // Normalizza i dati per gestire sia la struttura con attributes che quella flat
        const normalizedDonations = donations.map((donation) => {
          // Se i dati sono già flat (senza attributes), li normalizziamo
          if (!donation.attributes) {
            return {
              id: donation.id,
              attributes: {
                donationStatus: donation.donationStatus,
                amount: donation.amount,
                createdAt: donation.createdAt,
                metadata: donation.metadata,
                donation_goal: donation.donation_goal,
              },
            };
          }
          return donation;
        });

        setUserDonations(normalizedDonations);
      } catch (err) {
        console.error('Errore caricamento donazioni utente:', err);
      } finally {
        setLoadingDonationsUser(false);
      }
    };
    if (userData?.id) loadUserDonations();
  }, [userData]);

  return (
    <AccountPageShell title="Le mie donazioni" titleId="donazioni-title">
      <TagliandiFb onLoaded={setNRicevuteFb} />
      {loadingDonationsUser ? (
        <Flex justify="center" py={6}>
          <Spinner size="lg" color="#ffcc00" />
        </Flex>
      ) : userDonations.length === 0 &&
        nRicevuteFb > 0 ? null : userDonations.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          p={8}
          bg="#36393f"
          borderRadius="12px"
          boxShadow="sm"
          border="1px solid"
          borderColor="gray.600"
        >
          <WarningIcon boxSize={8} mb={4} color="#ffcc00" />
          <Text textAlign="center" mb={6} color="gray.300">
            Non hai ancora effettuato donazioni. Ogni contributo, grande o
            piccolo, aiuta a promuovere creatività, inclusione e iniziative
            culturali sul territorio.
          </Text>
          <Button
            as={Link}
            to="/sostienicil"
            bg="#ffcc00"
            color="black"
            _hover={{ bg: '#e6b800' }}
            borderRadius="12px"
            fontWeight="600"
          >
            Dona ora
          </Button>
        </Flex>
      ) : (
        <>
          {(() => {
            const succeededDonations = userDonations.filter((d) => {
              const data = d.attributes || d;
              const status = data.donationStatus;
              return status === 'succeeded';
            });

            // Separa le donazioni F&B da quelle normali
            const fbDonations = succeededDonations.filter((d) => {
              const data = d.attributes || d;
              return data.metadata?.donationItem;
            });

            const regularDonations = succeededDonations.filter((d) => {
              const data = d.attributes || d;
              return !data.metadata?.donationItem;
            });

            const renderDonationRow = (d) => {
              const data = d.attributes || d;
              const amt = data.amount || 0;
              const date = new Date(data.createdAt).toLocaleDateString(
                'it-IT',
                {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                }
              );
              const goal =
                data.metadata?.goalName ||
                data.donation_goal?.title ||
                'Donazione libera';
              const donationItem = data.metadata?.donationItem;

              return (
                <Tr
                  key={d.id}
                  _hover={{ bg: '#36393f' }}
                  transition="all 0.2s ease"
                >
                  <Td color="white" fontWeight="600">
                    €{amt.toFixed(2)}
                  </Td>
                  <Td color="gray.300" fontSize="sm">
                    {date}
                  </Td>
                  <Td>
                    <Badge
                      bg="#ffcc00"
                      color="black"
                      px={2}
                      py={1}
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight="600"
                    >
                      {goal}
                    </Badge>
                  </Td>
                  <Td>
                    {donationItem ? (
                      <Badge
                        bg="#3eb297"
                        color="white"
                        variant="outline"
                        borderRadius="full"
                        fontSize="xs"
                        fontWeight="600"
                      >
                        🍕 {donationItem}
                      </Badge>
                    ) : (
                      <Text color="gray.500" fontSize="xs">
                        -
                      </Text>
                    )}
                  </Td>
                  <Td>
                    <Text color="gray.400" fontSize="xs">
                      {d.id}
                    </Text>
                  </Td>
                  <Td>
                    <Button
                      size="sm"
                      bg="#36a9e1"
                      color="white"
                      onClick={() => handlePrintDonationReceipt(d)}
                      borderRadius="6px"
                      fontWeight="600"
                      fontSize="xs"
                      _hover={{
                        bg: '#2f95c7',
                        boxShadow: '0 4px 12px rgba(54, 169, 225, 0.3)',
                      }}
                      transition="all 0.2s ease"
                    >
                      PDF
                    </Button>
                  </Td>
                </Tr>
              );
            };

            return (
              <VStack spacing={6} align="stretch">
                {/* Donazioni Normali */}
                {regularDonations.length > 0 && (
                  <Box>
                    <Heading size="md" mb={4} color="white" fontWeight="700">
                      Donazioni
                    </Heading>
                    <Box
                      overflowX="auto"
                      borderRadius="8px"
                      border="1px solid"
                      borderColor="gray.600"
                    >
                      <Table variant="simple" colorScheme="gray" size="sm">
                        <Thead bg="#36393f">
                          <Tr>
                            <Th
                              color="white"
                              fontSize="xs"
                              fontWeight="700"
                              textTransform="uppercase"
                            >
                              Importo
                            </Th>
                            <Th
                              color="white"
                              fontSize="xs"
                              fontWeight="700"
                              textTransform="uppercase"
                              display={{ base: 'none', md: 'table-cell' }}
                            >
                              Data
                            </Th>
                            <Th
                              color="white"
                              fontSize="xs"
                              fontWeight="700"
                              textTransform="uppercase"
                            >
                              Destinazione
                            </Th>
                            <Th
                              color="white"
                              fontSize="xs"
                              fontWeight="700"
                              textTransform="uppercase"
                              display={{ base: 'none', lg: 'table-cell' }}
                            >
                              Prodotto
                            </Th>
                            <Th
                              color="white"
                              fontSize="xs"
                              fontWeight="700"
                              textTransform="uppercase"
                              display={{ base: 'none', xl: 'table-cell' }}
                            >
                              ID
                            </Th>
                            <Th
                              color="white"
                              fontSize="xs"
                              fontWeight="700"
                              textTransform="uppercase"
                            >
                              Azioni
                            </Th>
                          </Tr>
                        </Thead>
                        <Tbody>{regularDonations.map(renderDonationRow)}</Tbody>
                      </Table>
                    </Box>
                  </Box>
                )}

                {/* Donazioni F&B in Accordion */}
                {fbDonations.length > 0 && (
                  <Box
                    bg="#36393f"
                    borderRadius="12px"
                    p={3}
                    boxShadow="sm"
                    border="1px solid"
                    borderColor="gray.600"
                    mb={8}
                  >
                    <Accordion allowToggle>
                      <AccordionItem border="none">
                        <h2>
                          <AccordionButton
                            _hover={{ bg: '#2f3136', borderColor: '#ffcc00' }}
                            borderRadius="12px"
                            p={4}
                            border="1px solid"
                            borderColor="gray.600"
                          >
                            <Box flex="1" textAlign="left">
                              <Heading size="md" color="white" fontWeight="700">
                                🍕 Food & Beverage ({fbDonations.length})
                              </Heading>
                            </Box>
                            <AccordionIcon color="#ffcc00" />
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4} pt={4}>
                          <Box
                            overflowX="auto"
                            borderRadius="8px"
                            border="1px solid"
                            borderColor="gray.600"
                          >
                            <Table
                              variant="simple"
                              colorScheme="gray"
                              size="sm"
                            >
                              <Thead bg="#2f3136">
                                <Tr>
                                  <Th
                                    color="white"
                                    fontSize="xs"
                                    fontWeight="700"
                                    textTransform="uppercase"
                                  >
                                    Importo
                                  </Th>
                                  <Th
                                    color="white"
                                    fontSize="xs"
                                    fontWeight="700"
                                    textTransform="uppercase"
                                    display={{
                                      base: 'none',
                                      md: 'table-cell',
                                    }}
                                  >
                                    Data
                                  </Th>
                                  <Th
                                    color="white"
                                    fontSize="xs"
                                    fontWeight="700"
                                    textTransform="uppercase"
                                  >
                                    Destinazione
                                  </Th>
                                  <Th
                                    color="white"
                                    fontSize="xs"
                                    fontWeight="700"
                                    textTransform="uppercase"
                                    display={{
                                      base: 'none',
                                      lg: 'table-cell',
                                    }}
                                  >
                                    Prodotto
                                  </Th>
                                  <Th
                                    color="white"
                                    fontSize="xs"
                                    fontWeight="700"
                                    textTransform="uppercase"
                                    display={{
                                      base: 'none',
                                      xl: 'table-cell',
                                    }}
                                  >
                                    ID
                                  </Th>
                                  <Th
                                    color="white"
                                    fontSize="xs"
                                    fontWeight="700"
                                    textTransform="uppercase"
                                  >
                                    Azioni
                                  </Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {fbDonations.map(renderDonationRow)}
                              </Tbody>
                            </Table>
                          </Box>
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  </Box>
                )}
              </VStack>
            );
          })()}

          {/* CTA Section */}
          <Box
            p={6}
            bg="#36393f"
            borderRadius="12px"
            border="1px solid"
            borderColor="#36a9e1"
            textAlign="center"
            boxShadow="sm"
            mt={8}
          >
            <Heading size="md" color="white" mb={3} fontWeight="700">
              Vuoi fare di più?
            </Heading>
            <Text color="gray.300" mb={4}>
              Sostieni CIL con un abbonamento mensile o scopri i nostri progetti
              che hanno bisogno del tuo aiuto!
            </Text>
            <Button
              as={Link}
              to="/sostienicil"
              size="sm"
              bg="#ffcc00"
              color="black"
              borderRadius="6px"
              fontWeight="600"
              _hover={{
                bg: '#ffdd44',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(255, 204, 0, 0.3)',
              }}
              transition="all 0.3s ease"
              leftIcon={<WarningIcon />}
            >
              Scopri come sostenere CIL
            </Button>
          </Box>
        </>
      )}
    </AccountPageShell>
  );
};

export default MySubscriptions;
